import {
  ActivateCardInput,
  ActivateCardResultTypenames,
  AvailableCardAction,
  CardStatusEnum,
  CardTypeEnum,
  Mutation,
} from '@varo/graphql-schema-types';
import {
  NativeStackHeaderProps,
  RoutePath,
  Stack,
  useFocusEffect,
  useLocalSearchParams,
  usePathname,
  useRouter,
} from 'expo-router';
import { useFetchCards } from 'features/cardManagement/hooks/useFetchCards/useFetchCards';
import { useCallback, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { StyleSheet, View } from 'react-native';
import { useSafeAreaInsets } from 'react-native-safe-area-context';
import { requestContentCardsRefresh } from 'services/engagement/braze/braze';
import activateCard from 'services/graphql/mutations/ActivateCard';
import FixedCharacterInput from 'shared/components/vxl/input/FixedCharacterInput/FixedCharacterInput';
import { Variants } from 'shared/components/vxl/input/FixedCharacterInput/FixedCharacterInput.constants';
import { expirationDateSchema } from 'shared/components/vxl/input/schemas/card/expirationDate.schema';
import LoadingSpinner from 'shared/components/vxl/loading/LoadingSpinner/LoadingSpinner';
import ExpoNavigationBar from 'shared/components/vxl/navigation/ExpoNavigationBar/ExpoNavigationBar';
import BodyBaseText from 'shared/components/vxl/typography/primary/body/BodyBaseText';
import { isMobileBrowser } from 'shared/constants/platform';
import { useViewDidFocusScreenAnalyticsEvent } from 'shared/hooks/analytics/useViewDidFocusAnalytics';
import useActivationCard from 'shared/hooks/homeTiles/useActivationCard';
import { useEngagement } from 'shared/hooks/useBraze';
import useCancellableMutation, {
  AbortError,
} from 'shared/hooks/useCancellableMutation';
import theme from 'shared/styles/theme';
import { getUuid } from 'shared/utils/getUuid/getUuid';

const debit = 'debit';
const believe = 'believe';
const chargeCard = 'chargecard';
const CardActivationScreen = () => {
  const router = useRouter();
  const { cardId: cardIdParam } = useLocalSearchParams<{
    cardId?: string;
  }>();

  const pathName = usePathname();

  const { bottom } = useSafeAreaInsets();

  const getCardType = () => {
    if (pathName.toLowerCase().includes(debit)) {
      return CardTypeEnum.DebitCard;
    } else if (
      pathName.toLowerCase().includes(believe) ||
      pathName.toLowerCase().includes(chargeCard)
    ) {
      return CardTypeEnum.ChargeCard;
    }
  };

  const { data: sourceData, fetching: fetchingCardDetails } = useFetchCards({
    allowDuplicatePANs: true,
    requestPolicy: 'cache-and-network',
  });

  // Resolve cardId once from the cards list and pin it, rather than
  // re-deriving it on every render: a later refetch (e.g. triggered by this
  // screen's own activation mutation) must not reselect a different card.
  const [cardId, setCardId] = useState(cardIdParam);

  if (!cardId && sourceData?.cards) {
    const cardsByType = sourceData.cards.filter(
      (card) => card.type === getCardType(),
    );

    const card =
      cardsByType.find((card) =>
        card.actions.includes(AvailableCardAction.Activate),
      ) ??
      cardsByType[0] ??
      sourceData.cards[0];

    setCardId(card?.id);
  }

  const { t: translatedText } = useTranslation('myVaro', {
    keyPrefix: 'activationCard',
  });
  const [cardNumber, setCardNumber] = useState<string>('');
  const [description, setDescription] = useState<string>('cardDescription');
  const [inputState, setInputState] = useState(Variants.Otp);
  const [resultState, setResultState] = useState<boolean>(false);
  const [errorMessage, setErrorMessage] = useState<string | undefined>();
  const { logCustomEvent } = useEngagement();
  const {
    cardStatus,
    fetching: fetchingActivationCard,
    isPinSet,
    cardType,
    error: fetchCardsError,
    hasResponse,
  } = useActivationCard(cardId!);

  // wait until the card data has loaded before logging analytics so the cardType
  // is possibly correct for activate by card id
  // Note: we're not currently handling fetch card errors correctly on this page
  // we may not have the card for the id or have failed to load cards all together.
  const hasCardLoaded = hasResponse || fetchCardsError;

  // Visa may not have created a card UUID yet for Debit Cards just activated
  // by the time the user lands on the Card Activation Success Screen, so wallet
  // provisioning would fail. Hence, the Wallet button needs to be hidden
  // for Debit Cards.
  let shouldShowWalletButton =
    cardType === CardTypeEnum.ChargeCard ? 'true' : 'false';

  const analyticsCardType = getCardType() || cardType;
  useViewDidFocusScreenAnalyticsEvent(
    hasCardLoaded
      ? {
          key: inputState === Variants.Otp ? 'Last Four Digits' : 'Expiry Date',
          customAttributes: {
            card_type: analyticsCardType?.toString().toLowerCase() || 'unknown',
          },
        }
      : null,
  );

  useFocusEffect(
    useCallback(() => {
      if (cardStatus) {
        if (cardStatus === CardStatusEnum.Activated && isPinSet) {
          logCustomEvent(
            'BRAZETRIGGER, card activation default success screen viewed',
          );
          requestContentCardsRefresh();
          router.replace({
            pathname: `/myvaro/card/activate-card-success`,
            params: {
              cardId: cardId,
              cardType: cardType,
              title: translatedText('defaultPinSetSuccessTitle'),
              message: translatedText(
                cardType === CardTypeEnum.DebitCard
                  ? 'defaultDebitActivationSuccessMessage'
                  : 'defaultBelievePinSetSuccessMessage',
              ),
              shouldShowWalletButton: 'true', // Card already activated, so show wallet button.
            },
          });
        }
        if (cardStatus === CardStatusEnum.Activated && !isPinSet) {
          router.replace({
            pathname: `/myvaro/card/activate-card-success`,
            params: {
              cardId: cardId,
              cardType: cardType,
              defaultSuccess: 'yes',
              shouldShowWalletButton: 'true', // Card already activated, so show wallet button.
            },
          });
        }
      }
    }, [cardId, cardStatus, cardType, isPinSet, translatedText, router]),
  );

  const [, executeActivateCard] =
    useCancellableMutation<Mutation>(activateCard);

  const activateYourCard = async (token: string, input: ActivateCardInput) => {
    setResultState(true);

    let cardActivationResult: Mutation['activateCard'] | undefined;

    try {
      const result = await executeActivateCard({ token, input });
      cardActivationResult = result.data?.activateCard;
    } catch (error) {
      if (error instanceof AbortError) {
        return;
      }
    }

    let pathname: RoutePath = '/myvaro/card/activate-card-error';

    const isSuccess =
      cardActivationResult?.__typename ===
      ActivateCardResultTypenames.ActivateCardSuccess;

    setResultState(false);
    setDescription('pinDescription');

    if (isSuccess && isPinSet) {
      router.replace({
        pathname: `/myvaro/card/activate-card-success`,
        params: {
          cardId: cardId,
          cardType: cardType,
          title: translatedText('defaultPinSetSuccessTitle'),
          message: translatedText(
            cardType === CardTypeEnum.DebitCard
              ? 'defaultDebitActivationSuccessMessage'
              : 'defaultBelievePinSetSuccessMessage',
          ),
          shouldShowWalletButton: shouldShowWalletButton,
        },
      });
      return;
    }

    if (isSuccess) {
      pathname = `/myvaro/card/new-pin/[cardId]`;
    }

    router.replace({
      pathname,
      params: {
        title: cardActivationResult?.title,
        message: cardActivationResult?.message,
        type: cardActivationResult?.__typename,
        cardId: cardId!,
        cardType: cardType,
        shouldShowWalletButton: shouldShowWalletButton,
      },
    });
  };

  const handleSendOtp = async (key: string) => {
    if (key.length === 4) {
      if (inputState === Variants.Otp) {
        setCardNumber(key);
        setInputState(Variants.Date);
        setDescription('dateDescription');
      } else {
        const expirationDate = `${key.slice(0, 2)}/${key.slice(2, 4)}`;
        if (!expirationDateSchema.safeParse(expirationDate).success) {
          setErrorMessage(translatedText('errorExpDate'));
          return;
        }
        setErrorMessage(undefined);
        const token = getUuid();
        const input: ActivateCardInput = {
          cardId: cardId!,
          last4: cardNumber,
          expiration: key,
        };
        await activateYourCard(token, input);
      }
    }
  };

  return (
    <View style={[styles.container, { paddingBottom: bottom }]}>
      {!fetchingCardDetails && !fetchingActivationCard ? (
        <>
          <Stack.Screen
            options={{
              title: translatedText('title'),
              headerBackVisible: true,
              headerShown: true,
              header: (props: NativeStackHeaderProps) => (
                <ExpoNavigationBar {...props} />
              ),
            }}
          />
          <BodyBaseText style={styles.text} numberOfLines={0}>
            {translatedText(description)}
          </BodyBaseText>
          <View style={styles.input}>
            <FixedCharacterInput
              key={inputState}
              variant={inputState}
              inputBoxLength={4}
              isCustomNumPad={!isMobileBrowser}
              showNumPadSubmitButton
              sendOtp={handleSendOtp}
              numPadSubmitButtonLabel={translatedText('next')}
              numPadSubmitButtonLoading={resultState}
              errorMessage={inputState === Variants.Date ? errorMessage : ''}
            />
          </View>
        </>
      ) : (
        <View style={styles.loaderContainer}>
          <LoadingSpinner />
        </View>
      )}
    </View>
  );
};

const styles = StyleSheet.create({
  container: {
    backgroundColor: theme.color.background.primary,
    flex: 1,
  },
  input: {
    flex: 1,
    marginHorizontal: theme.spacing.m,
  },
  loaderContainer: {
    alignItems: 'center',
    backgroundColor: theme.color.background.primary,
    flex: 1,
    justifyContent: 'center',
    padding: theme.spacing.m,
  },
  text: {
    paddingLeft: theme.spacing.m,
    paddingVertical: theme.spacing.m,
  },
});

export default CardActivationScreen;
